import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ModeNavigation.css';

interface Mode {
  id: string;
  icon: React.ReactNode;
  nameKey: string;
  path: string;
}

const IconComprehension = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 4.5h5.5c1.1 0 2 .9 2 2v10a1.5 1.5 0 0 0-1.5-1.5H3V4.5z" />
    <path d="M17 4.5h-5.5c-1.1 0-2 .9-2 2v10a1.5 1.5 0 0 1 1.5-1.5H17V4.5z" />
  </svg>
);

const IconVocabulary = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="11" height="13" rx="1.5" />
    <path d="M6 4V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-1" />
    <path d="M6 8.5h5" />
    <path d="M6 11.5h3" />
  </svg>
);

const IconShadowing = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 3v10" />
    <rect x="8" y="3" width="4" height="7" rx="2" />
    <path d="M6 10a4 4 0 0 0 8 0" />
    <path d="M10 13v4" />
    <path d="M7 17h6" />
  </svg>
);

const IconQuiz = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2.5" width="12" height="15" rx="1.5" />
    <path d="M7 6.5h6" />
    <path d="M7 9.5h6" />
    <path d="M7 12.5h4" />
  </svg>
);

const IconDictation = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" />
    <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" />
  </svg>
);

const IconQuest = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2.5H5.5A1.5 1.5 0 0 0 4 4v12a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 16 16V4.5L14 2.5z" />
    <path d="M14 2.5V5h2.5" />
    <path d="M12 11l-3.5 3.5L7 13" />
    <path d="M7 8h6" />
  </svg>
);

const modes: Mode[] = [
  { id: 'comprehension', icon: <IconComprehension />, nameKey: 'modes.comprehension', path: 'comprehension' },
  { id: 'vocabulary', icon: <IconVocabulary />, nameKey: 'modes.vocabulary', path: 'vocabulary' },
  { id: 'shadowing', icon: <IconShadowing />, nameKey: 'modes.shadowing', path: 'shadowing' },
  { id: 'quiz', icon: <IconQuiz />, nameKey: 'modes.quiz', path: 'quiz' },
  { id: 'dictation', icon: <IconDictation />, nameKey: 'modes.dictation', path: 'dictation' },
  { id: 'writing', icon: <IconQuest />, nameKey: 'modes.writing', path: 'writing' },
];

const ModeNavigation: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { t } = useTranslation('learning');

  return (
    <nav className="mode-navigation">
      {/* Desktop Side Navigation */}
      <div className="mode-navigation__desktop">
        {modes.map((mode) => (
          <NavLink
            key={mode.id}
            to={`/courses/${courseId}/${mode.path}`}
            className={({ isActive }) =>
              `mode-navigation__item ${isActive ? 'mode-navigation__item--active' : ''}`
            }
          >
            <span className="mode-navigation__icon">{mode.icon}</span>
            <span className="mode-navigation__name">{t(mode.nameKey)}</span>
          </NavLink>
        ))}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="mode-navigation__mobile">
        {modes.map((mode) => (
          <NavLink
            key={mode.id}
            to={`/courses/${courseId}/${mode.path}`}
            className={({ isActive }) =>
              `mode-navigation__mobile-item ${isActive ? 'mode-navigation__mobile-item--active' : ''}`
            }
          >
            <span className="mode-navigation__icon">{mode.icon}</span>
            <span className="mode-navigation__mobile-name">{t(mode.nameKey)}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export default ModeNavigation;
